import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const PrepacksIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="Prepacks allow you to create a set size run of products for purchase."
          description={{
            text: "Introduction to prepacks",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="You can set up a prepack on a new product listing or an existing product by following these steps:"
          description={{
            text: "Instructions header",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to the <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "First step to set up a prepack",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select the product you want to edit or create a new listing."
              description={{
                text: "Second step to set up a prepack",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Under <BoldFormatting>Does this product come in multiple options, like different sizes or colors?</BoldFormatting>, select <BoldFormatting>Yes</BoldFormatting> and add the required sizes, such as S, M, L, XL."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to set up a prepack",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once you’ve saved these product options, select <BoldFormatting>Prepacks</BoldFormatting> under <BoldFormatting>How would you like to sell this product?</BoldFormatting>, then select <BoldFormatting>Create prepack</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to set up a prepack",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Enter the name of your prepack (ex. Pack A), your preferred size ratio (ex. 1-2-2-1), and select a size range from the dropdown (ex. S-XL). Size ranges will automatically populate based on your size options and the size ratio you enter."
              description={{
                text: "Fifth step to set up a prepack",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={6}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="To offer more than one prepack for the same listing, select the (+) button next to the <BoldFormatting>Pack Name</BoldFormatting> field."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Sixth step to set up a prepack",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={7}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="After adding all prepack information, <BoldFormatting>save</BoldFormatting> the changes, and your listing will now be offered as a prepack."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Seventh step to set up a prepack",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Note that if you’re editing your size ratio in a bulk upload spreadsheet, ensure there are no extra white spaces. For example, use “1-2-2-2-1;XS-S-M-L-XL” and specify each size individually (not “S-L”, but “S-M-L”)."
          description={{
            text: "Note about bulk upload",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
